import { useQuery } from '@tanstack/react-query';
import apiClient from '../../../services/apiClientService';
import { Routes } from '../../../routes/RouteConstants';
import useErrorHandler from '../../../hooks/useErrorHandler';
import { useTenant } from '../../../contexts/TenantContext';

/**
 * The signed-in user (`GET /users/me`), for the top bar's „who is signed in”. `enabled` waits for
 * `staff/me`, so a session that has run out is reported once, by that call, not twice.
 *
 * Kept only while the dashboard is open (`gcTime: 0`), as `useGetStaffMe`: after signing out and
 * in as somebody else, an answer cached from before would be the other user's.
 */
const useGetSignedInUser = ({ enabled = true } = {}) => {
  const { tenantId } = useTenant();
  const { handleError } = useErrorHandler();

  return useQuery({
    queryKey: ['signedInUser', tenantId],
    queryFn: async () => {
      try {
        const response = await apiClient.get(`/v1/tenants/${tenantId}/users/me`);
        return response.data;
      } catch (error) {
        handleError(error, Routes.DASHBOARD);
        throw error;
      }
    },
    enabled: !!tenantId && enabled,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export default useGetSignedInUser;
